import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { FaMapMarkerAlt, FaCheckCircle, FaUser, FaUsers, FaClock, FaLocationArrow } from 'react-icons/fa';
import { CustomButton10, CustomButton10Ca, CustomButton10Co } from '../../Templates';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../hooks/ToastContext';
import { supabase } from '../../data/supabaseClient';
import Spinner from '../../common/Spinner';

// Import our race condition safe helpers
import { 
  handleImHereAction, 
  startWaitingTimer, 
  handleRaceCondition, 
  retryOperation 
} from '../../../utils/rideActionsHelpers';

const ArrivalConfirmation = ({ 
  ride, 
  currentUser, 
  otherUser, 
  userRole, 
  onUpdate, 
  addSystemMessage, 
  createNotification 
}) => {
  const { t } = useTranslation('common');
  const { addToast } = useToast();
  
  const [isConfirming, setIsConfirming] = useState(false);
  const [showLocationDialog, setShowLocationDialog] = useState(false);
  const [showTimerDialog, setShowTimerDialog] = useState(false);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [isStartingTimer, setIsStartingTimer] = useState(false);
  
  // Debug: Log when component mounts/unmounts
  useEffect(() => {
    console.log('ArrivalConfirmation mounted for ride:', ride?.id);
    return () => {
      console.log('ArrivalConfirmation unmounted for ride:', ride?.id);
    };
  }, [ride?.id]);
  
  // Debug: Log when dialog state changes
  useEffect(() => {
    console.log('Location dialog state changed:', showLocationDialog, 'for ride:', ride?.id);
  }, [showLocationDialog, ride?.id]);

  // Check arrival status
  const isDriverUser = currentUser.id === ride.driver_id;
  const hasUserArrived = isDriverUser ? !!ride.driver_arrival_time : !!ride.passenger_arrival_time;
  const hasOtherArrived = isDriverUser ? !!ride.passenger_arrival_time : !!ride.driver_arrival_time;
  const bothArrived = hasUserArrived && hasOtherArrived;
  
  // Check if user marked "I'm here" (new status before timer)
  const hasUserMarkedHere = isDriverUser ? !!ride.driver_marked_here_at : !!ride.passenger_marked_here_at;
  const hasOtherMarkedHere = isDriverUser ? !!ride.passenger_marked_here_at : !!ride.driver_marked_here_at;
  const timerStarted = !!ride.timer_started_at;

  // Get current location
  const getCurrentLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error(t('ride_actions.geolocation_not_supported')));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude, accuracy } = position.coords;
          
          // Only accept if accuracy is within 50 meters
          if (accuracy > 50) {
            reject(new Error(t('ride_actions.location_accuracy_low')));
            return;
          }

          try {
            // Reverse geocode to get address (optional)
            const response = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
            );
            const data = await response.json();
            
            resolve({
              latitude,
              longitude,
              accuracy,
              timestamp: new Date().toISOString(),
              address: data.display_name || t('ride_actions.address_not_available')
            });
          } catch (error) {
            // If reverse geocoding fails, still return location
            resolve({
              latitude,
              longitude,
              accuracy,
              timestamp: new Date().toISOString(),
              address: t('ride_actions.address_lookup_failed')
            });
          }
        },
        (error) => {
          reject(error);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      );
    });
  };

  // Handle "I'm here" - now using race condition safe function
  const handleImHere = async (withLocation = false) => {
    setIsConfirming(true);
    
    try {
      let locationProof = null;
      
      // Get location if requested
      if (withLocation) {
        setGettingLocation(true);
        try {
          locationProof = await getCurrentLocation();
        } catch (locError) {
          console.error('Location error:', locError);
          addToast({
            type: 'warning',
            title: t('ride_actions.location_not_available'),
            description: locError.message || t('ride_actions.location_not_available_desc')
          });
        }
        setGettingLocation(false);
      }

      // Use race condition safe "I'm here" action
      const operation = () => handleImHereAction(ride.id, currentUser.id, locationProof);
      const result = await retryOperation(operation, 3, 1000);
      
      if (result.conflictDetected) {
        // Handle race condition
        await handleRaceCondition(onUpdate, addToast, t, result);
        setShowLocationDialog(false);
        return;
      }
      
      if (!result.success) {
        throw new Error(result.message || t('ride_actions.failed_mark_here'));
      }
      
      // Update local state with latest ride data
      if (result.rideData && onUpdate) {
        onUpdate(result.rideData);
      }
      
      // Handle next action based on database response
      switch (result.nextAction) {
        case 'show_ready_confirmation':
          // Both users are now present
          await addSystemMessage(
            `Both users are now present at the meeting point` + 
            (locationProof ? ' (location shared)' : '')
          );
          
          addToast({
            type: 'success',
            title: t('ride_actions.both_present'),
            description: t('ride_actions.both_present_ready_to_start')
          });
          break;
          
        case 'start_waiting_timer':
          // Only current user is present
          await addSystemMessage(
            `${userRole} marked as here at meeting point` + 
            (locationProof ? ' (location shared)' : '')
          );
          
          addToast({
            type: 'success',
            title: t('ride_actions.marked_as_here'),
            description: t('ride_actions.other_notified_here')
          });
          break;
      }

      setShowLocationDialog(false);
      
      
    } catch (error) {
      console.error('Error in handleImHere:', error);
      addToast({
        type: 'error',
        title: t('ride_actions.error'),
        description: error.message || t('ride_actions.failed_mark_here')
      });
    } finally {
      setIsConfirming(false);
      setGettingLocation(false);
    }
  };

  // Handle starting the timer manually - now using race condition safe function
  const handleStartTimer = async () => {
    setIsStartingTimer(true);
    
    try {
      // Use race condition safe timer start
      const operation = () => startWaitingTimer(ride.id, currentUser.id, ride.meeting_duration_minutes || 3);
      const result = await retryOperation(operation, 3, 1000);
      
      if (!result.success) {
        throw new Error(result.message || t('ride_actions.failed_start_timer'));
      }
      
      // Update local state with latest ride data
      if (result.rideData && onUpdate) {
        onUpdate(result.rideData);
      }
      
      // Create notifications and system messages
      const otherUserId = currentUser.id === ride.driver_id ? 
        ride.passenger_id : ride.driver_id;
      
      await createNotification(
        otherUserId,
        'timer_started',
        `Timer started by ${userRole} (${ride.meeting_duration_minutes || 3} minutes)`,
        ride.id
      );

      await addSystemMessage(
        `Timer started by ${userRole} - waiting ${ride.meeting_duration_minutes || 3} minutes`
      );

      addToast({
        type: 'info',
        title: t('ride_actions.timer_started'),
        description: t('ride_actions.waiting_timer_desc', { minutes: ride.meeting_duration_minutes || 3 })
      });

      setShowTimerDialog(false);
      
    } catch (error) {
      console.error('Error starting timer:', error);
      addToast({
        type: 'error',
        title: t('ride_actions.error'),
        description: error.message || t('ride_actions.failed_start_timer')
      });
    } finally {
      setIsStartingTimer(false);
    }
  };

  // Get meeting time display
  const getMeetingTimeDisplay = () => {
    if (!ride.meeting_time) return null;
    
    const meetingTime = new Date(ride.meeting_time);
    const now = new Date();
    const isTime = now >= meetingTime;
  };

  // Render arrival status - now shows "marked here" status
  const renderArrivalStatus = () => (
    <div className="RAC-arrival-status">
      <div className={`RAC-user-arrival ${hasUserMarkedHere ? 'RAC-marked-here' : 'RAC-pending'}`}>
        <FaUser size={14} />
        <span>
          {userRole}: {hasUserMarkedHere ? t('ride_actions.here') : t('ride_actions.not_here')}
        </span>
      </div>
      <div className={`RAC-user-arrival ${hasOtherMarkedHere ? 'RAC-marked-here' : 'RAC-pending'}`}>
        <FaUser size={14} />
        <span>
          {userRole === t('ride_actions.driver') ? t('ride_actions.passenger') : t('ride_actions.driver')}: {hasOtherMarkedHere ? t('ride_actions.here') : t('ride_actions.not_here')}
        </span>
      </div>
    </div>
  );

  // Render location confirmation will be handled by separate component

  // Main render
  return (
    <div className="RAC-arrival-confirmation">
      <div className="RAC-arrival-content">
        {getMeetingTimeDisplay()}
        {renderArrivalStatus()}
        
        {timerStarted ? (
          // Timer already started, waiting phase will handle this
          <div className="RAC-timer-already-started">
            <FaClock size={16} />
            <span>{t('ride_actions.timer_already_running')}</span>
          </div>
        ) : (
          <div className="RAC-arrival-actions">
            {!hasUserMarkedHere && (
              <div className="RAC-arrival-step">
                <div className="RAC-arrival-info">
                  <p>{t('ride_actions.let_other_know_here')}</p>
                </div>
                <CustomButton10
                  onClick={() => setShowLocationDialog(true)}
                  label={t('ride_actions.im_here')}
                  disabled={isConfirming}
                >
                  <FaLocationArrow />
                </CustomButton10>
              </div>
            )}
            
            {/* Show Start Timer button only when user is here but other isn't */}
            {hasUserMarkedHere && !hasOtherMarkedHere && (
              <div className="RAC-timer-step">
                <div className="RAC-timer-info">
                  {ride.meeting_time && (
                    <p className="RAC-meeting-time-reminder">
                      {t('ride_actions.meeting_time_is', { 
                        time: new Date(ride.meeting_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) 
                      })}
                    </p>
                  )}
                  <p>{t('ride_actions.start_timer_when_ready')}</p>
                </div>
                <CustomButton10Co
                  onClick={() => setShowTimerDialog(true)}
                  label={t('ride_actions.start_timer')}
                  disabled={isStartingTimer}
                >
                  {isStartingTimer ? <Spinner size="small" showText={false} /> : <FaClock />}
                </CustomButton10Co>
              </div>
            )}
            
            {/* Show waiting message to the user who hasn't marked here yet */}
            {hasOtherMarkedHere && !hasUserMarkedHere && (
              <div className="RAC-waiting-for-arrival">
                <p>{t('ride_actions.other_user_waiting', { 
                  role: userRole === t('ride_actions.driver') ? t('ride_actions.passenger') : t('ride_actions.driver') 
                })}</p>
              </div>
            )}
          </div>
        )}
      </div>
      
      {showLocationDialog && (
        <LocationConfirmDialog
          key={`location-dialog-${ride?.id || 'new'}`}
          onCancel={() => setShowLocationDialog(false)}
          onConfirm={(shareLocation) => handleImHere(shareLocation)}
          isConfirming={isConfirming}
          gettingLocation={gettingLocation}
          userRole={userRole}
        />
      )}
      
      {showTimerDialog && (
        <TimerStartDialog
          key={`timer-dialog-${ride?.id || 'new'}`}
          onCancel={() => setShowTimerDialog(false)}
          onConfirm={handleStartTimer}
          isStarting={isStartingTimer}
          userRole={userRole}
          waitMinutes={ride.meeting_duration_minutes || 3}
        />
      )}
    </div>
  );
};

// Location Confirmation Dialog Component - Renders as fullscreen modal
const LocationConfirmDialog = ({
  onCancel,
  onConfirm,
  isConfirming,
  gettingLocation,
  userRole
}) => {
  const { t } = useTranslation('common');
  const [shareLocation, setShareLocation] = useState(false);

  return ReactDOM.createPortal(
    <div 
      className="RAC-modal-overlay-v2" 
      onClick={(e) => {
        // Only close if clicking directly on overlay
        if (e.target === e.currentTarget && !isConfirming) {
          onCancel();
        }
      }}
    >
      <div className="RAC-modal2-v2">
        <div className="RAC-location-dialog">
          <div className="RAC-dialog-content">
            <h4>{t('ride_actions.confirm_your_arrival')}</h4>
            <p>Let the {userRole === 'Driver' ? 'passenger' : 'driver'} know you've arrived.</p>
            
            <div className="RAC-location-option">
              <label className="RAC-checkbox-label">
                <input
                  type="checkbox"
                  checked={shareLocation}
                  onChange={(e) => setShareLocation(e.target.checked)}
                  disabled={isConfirming}
                />
                <span>{t('ride_actions.share_location_proof')}</span>
              </label>
              <p className="RAC-location-help">
                {t('ride_actions.optional_gps_proof')}
              </p>
            </div>
            
            <div className="RAC-dialog-actions">
              <CustomButton10Ca
                onClick={onCancel}
                label={t('buttons.cancel')}
                disabled={isConfirming}
              />
              <CustomButton10Co
                onClick={() => onConfirm(shareLocation)}
                disabled={isConfirming || gettingLocation}
              >
                {isConfirming || gettingLocation ? (
                  <Spinner size="small" showText={false} />
                ) : (
                  t('ride_actions.confirm_arrival')
                )}
              </CustomButton10Co>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

// Timer Start Dialog Component
const TimerStartDialog = ({
  onCancel,
  onConfirm,
  isStarting,
  userRole,
  waitMinutes
}) => {
  const { t } = useTranslation('common');

  return ReactDOM.createPortal(
    <div 
      className="RAC-modal-overlay-v2" 
      onClick={(e) => {
        if (e.target === e.currentTarget && !isStarting) {
          onCancel();
        }
      }}
    >
      <div className="RAC-modal2-v2">
        <div className="RAC-timer-start-dialog">
          <div className="RAC-dialog-content">
            <h4>{t('ride_actions.start_waiting_timer')}</h4>
            <p>The timer will run for {waitMinutes} minutes.</p>
            <p className="RAC-timer-notice">
              {t('ride_actions.other_user_will_be_notified')}
            </p>
            
            <div className="RAC-dialog-actions">
              <CustomButton10Ca
                onClick={onCancel}
                label={t('buttons.cancel')}
                disabled={isStarting}
              />
              <CustomButton10Co
                onClick={onConfirm}
                disabled={isStarting}
              >
                {isStarting ? (
                  <Spinner size="small" showText={false} />
                ) : (
                  t('ride_actions.start_timer')
                )}
              </CustomButton10Co>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ArrivalConfirmation;